import { cn, EmptyState, type EmptyStateSize } from "@norican/react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

import { useT } from "@/i18n";

export interface EmptyStateBlockProps extends Omit<ComponentPropsWithoutRef<"div">, "title"> {
  /** Headline. Defaults to the translated "Nothing here yet". */
  title?: ReactNode;
  /** Supporting line. Pass `null` to show the title alone. */
  description?: ReactNode | null;
  /** Illustration or `<Icon>` above the title. */
  icon?: ReactNode;
  /** Buttons under the text — typically the one thing the user should do next. */
  actions?: ReactNode;
  /** Vertical scale. Defaults to `md`; use `sm` inside a table or card body. */
  size?: EmptyStateSize;
}

/**
 * The standard "there is nothing to show" state.
 *
 * `DataGrid` deliberately has no empty slot, so list features render this
 * instead of the grid when `items.length === 0`. Wrapping the design system
 * `EmptyState` here means the default copy is translated and every empty list
 * in the portal reads the same way.
 */
export function EmptyStateBlock({
  title,
  description,
  icon,
  actions,
  size = "md",
  className,
  ...props
}: EmptyStateBlockProps) {
  const { t } = useT();

  return (
    <EmptyState
      className={cn("w-full", className)}
      icon={icon}
      title={title ?? t("state.emptyTitle")}
      description={description === null ? undefined : (description ?? t("state.emptyHint"))}
      actions={actions}
      size={size}
      {...props}
    />
  );
}
